/* Copyright (c) 2018 Scaper */

import { UserInfo } from "@firebase/auth-types";
import * as cx from "classnames";
import * as React from "react";
import { connect } from "react-redux";
import PhoneInput from "react-phone-number-input/native";
import { RouteComponentProps, withRouter } from "react-router";
import { IApi } from "../../api/api";
import { ConfirmPhoneNumberPath, ISetPhoneNumberRouteParams } from "../../paths/pathIndex";
import { IAppState } from "../appState";
import { CloseButton } from "../modal/closeButton";
import { IUserPhoneNumberDocument } from "../userState";

import "react-phone-number-input/rrui.css";
import "react-phone-number-input/style.css";
import "./setPhoneNumber.scss";

interface IOwnProps {
    api: IApi;
    routeParams: ISetPhoneNumberRouteParams;
    userInfo: UserInfo;
}

interface IStateProps {
    phoneNumberDocument: IUserPhoneNumberDocument | false | undefined;
}

type IProps = IOwnProps & IStateProps;

interface IState {
    proposedPhoneNumber: string | undefined;
    updateInProgress: boolean;
}

class UnconnectedSetPhoneNumber extends React.PureComponent<IProps & RouteComponentProps<any>, IState> {
    private static readonly NUMBER_FORMAT = /^\+1\d{10}$/;
    private static readonly NUMBER_LENGTH = 12;

    private unsubscribeFromPhoneNumber: (() => void) | undefined;

    public state: IState = {
        proposedPhoneNumber: undefined,
        updateInProgress: false,
    };

    public render() {
        return (
            <>
                <h2>
                    {this.renderTitle()}

                    <CloseButton />
                </h2>

                <div className="divider"></div>

                <div className="section">
                    <div className="row">
                        <div className="col s12 m10 push-m1 l8 push-l2">
                            <div className="scpr-SetPhoneNumber-form card-panel">
                                {this.renderForm()}
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    public componentDidMount() {
        const { api, userInfo } = this.props;
        this.unsubscribeFromPhoneNumber = api.userService.subscribeToPhoneNumber(userInfo);
    }

    public componentWillUnmount() {
        if (this.unsubscribeFromPhoneNumber != null) {
            this.unsubscribeFromPhoneNumber();
        }
    }

    private renderTitle() {
        const { phoneNumberDocument } = this.props;

        if (phoneNumberDocument == null) {
            return "Loading…";
        }

        if (phoneNumberDocument === false) {
            return "Register phone number";
        }

        return "Change phone number";
    }

    private renderForm() {
        const { proposedPhoneNumber, updateInProgress } = this.state;
        const { reason, warn, valid } = this.isProposedPhoneNumberValid();
        const submitDisabled = !valid || updateInProgress;
        return (
            <>
                <PhoneInput
                    country="US"
                    countries={[ "US" ]}
                    disabled={updateInProgress}
                    displayInitialValueAsLocalNumber
                    error={reason}
                    indicateInvalid={warn}
                    onChange={value => this.setState({ proposedPhoneNumber: value }) }
                    placeholder="Start typing a phone number"
                    value={proposedPhoneNumber}
                />

                <button
                    className={cx("btn-large waves-effect waves-light right", { disabled: submitDisabled })}
                    onClick={this.submitPhoneNumber}
                    type="submit"
                >
                    Send verification PIN
                    <i className="material-icons right">send</i>
                </button>
            </>
        );
    }

    private isProposedPhoneNumberValid(): { reason?: string, valid: boolean, warn?: boolean } {
        const { proposedPhoneNumber } = this.state;

        if (proposedPhoneNumber == null) {
            return {
                reason: "Phone number required",
                valid: false,
            };
        }

        if (proposedPhoneNumber.length < UnconnectedSetPhoneNumber.NUMBER_LENGTH) {
            return {
                reason: "Too short",
                valid: false,
            };
        }

        if (proposedPhoneNumber.length > UnconnectedSetPhoneNumber.NUMBER_LENGTH) {
            return {
                reason: "Too long",
                valid: false,
                warn: true,
            };
        }

        const isFormatted = UnconnectedSetPhoneNumber.NUMBER_FORMAT.test(proposedPhoneNumber);
        if (!isFormatted) {
            return {
                reason: "Invalid format",
                valid: false,
                warn: true,
            };
        }

        return { valid: true };
    }

    private submitPhoneNumber = () => {
        const { api } = this.props;
        const { proposedPhoneNumber } = this.state;
        const { valid } = this.isProposedPhoneNumberValid();

        if (!valid) {
            return;
        }

        this.setState({ updateInProgress: true });
        api.userService.setPhoneNumber(proposedPhoneNumber!).then(this.goToConfirmPhoneNumber);
    };

    private goToConfirmPhoneNumber = () => {
        const { history, routeParams } = this.props;
        const { from } = routeParams;
        const path = ConfirmPhoneNumberPath.of({ from });
        history.replace(path);
    }
}

function mapStateToProps(state: IAppState): IStateProps {
    const { user } = state;
    const { phoneNumberDocument } = user;
    return { phoneNumberDocument };
}

export const SetPhoneNumber = withRouter(connect<IStateProps, {}, IOwnProps, IAppState>(mapStateToProps)(UnconnectedSetPhoneNumber));
